import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import useCourseStore from '../stores/useCourseStore';
import { initializeSocket } from '../utils/socket';
import LMSHeader from '../components/LMSHeader';

const PaymentVerification = () => {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const { fetchMyCourses, verifyPayment } = useCourseStore();

  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [countdown, setCountdown] = useState(30);
  const [attempts, setAttempts] = useState(0);

  const params = new URLSearchParams(window.location.search);
  const reference = params.get('reference');
  const courseId = params.get('courseId');

  useEffect(() => {
    if (!user) return;

    // Initialize socket once
    if (!window.socket) {
      window.socket = initializeSocket(token);
    }

    // Webhook real-time confirmation listener
    const handlePaymentVerified = (data) => {
      if (data && data.enrollmentId && data.courseId === courseId) {
        setVerificationStatus('success');
        setTimeout(() => {
          fetchMyCourses();
          navigate('/student/my-courses');
        }, 2500);
      }
    };

    window.socket?.on('payment_verified', handlePaymentVerified);

    // Countdown and fallback verification logic
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          retryManualVerification();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      window.socket?.off('payment_verified', handlePaymentVerified);
      clearInterval(interval);
    };
  }, [user]);

  // Manual & auto verification retry logic
  const retryManualVerification = async () => {
    if (!reference || attempts >= 5) {
      setVerificationStatus('failed');
      return;
    }

    setAttempts((prev) => prev + 1);
    try {
      const result = await verifyPayment(reference);
      if (result?.success) {
        setVerificationStatus('success');
        setTimeout(() => {
          fetchMyCourses();
          navigate('/student/my-courses');
        }, 2500);
      } else {
        // Retry after 30 seconds
        setTimeout(retryManualVerification, 30000);
      }
    } catch (error) {
      console.error('Retry verification error:', error);
      setTimeout(retryManualVerification, 30000);
    }
  };

  // UI states stay the same
  if (verificationStatus === 'verifying') {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-500 mb-6 mx-auto"></div>
            <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">
              Verifying Payment...
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Please wait while we verify your payment. Retrying automatically...
            </p>
            <div className="w-64 h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full mx-auto">
              <div
                className="h-2 bg-primary-500 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${((30 - countdown) / 30) * 100}%` }}
              ></div>
            </div>
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
              Attempt {attempts + 1} of 5
            </p>
          </div>
        </div>
      </>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-16 h-16 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
              Payment Successful!
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
              You are now enrolled in the course. Redirecting to your courses...
            </p>
          </div>
        </div>
      </>
    );
  }

  if (verificationStatus === 'failed') {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 bg-danger-100 dark:bg-danger-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-16 h-16 text-danger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
              Payment Verification Failed
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-8">
              We couldn't verify your payment. Please try again or contact support.
            </p>
            <button
              onClick={retryManualVerification}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm"
            >
              Retry Verification
            </button>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default PaymentVerification;